// components/ChatInput.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { TextField } from 'ui';

import classes from '../Chat.module.scss';
import screen from '../ChatScreen.module.scss';
import { validateChatMessageText } from '../utils/contactFilter';

interface Props {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const ChatInput: React.FC<Props> = ({
  onSendMessage,
  disabled = false,
  placeholder = 'Напишите сообщение...',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = useCallback((value: string) => {
    setInputValue(value);
    if (validationError) {
      setValidationError(null);
    }
  }, [validationError]);

  const handleSend = useCallback(() => {
    const trimmedText = inputValue.trim();

    if (!trimmedText || disabled) {
      return;
    }

    const error = validateChatMessageText(trimmedText);
    if (error) {
      setValidationError(error);
      return;
    }

    onSendMessage(trimmedText);
    setInputValue('');
    setValidationError(null);

    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [inputValue, disabled, onSendMessage]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };

    textarea.addEventListener('keydown', handleKeyDown);
    return () => textarea.removeEventListener('keydown', handleKeyDown);
  }, [handleSend]);

  const sendDisabled = inputValue.trim() === '' || disabled;

  return (
    <div className={`${classes['chat-input']} ${screen.composer}`}>
      {validationError ? (
        <p className={classes['chat-input-error']} role='alert'>
          {validationError}
        </p>
      ) : null}
      <div className={`${classes['chat-input-wrapper']} ${screen.composerRow}`}>
        <div className={classes['chat-input-field']}>
          <TextField
            ref={textareaRef}
            value={inputValue}
            placeholder={placeholder}
            tag='textarea'
            variant='clear'
            size='clear'
            inputContainerClassName={classes['inputContainer']}
            inputClassName={`${classes['input']} ${screen.field}`}
            onChange={handleChange}
            widthFullSize={true}
            disabled={disabled}
          />
        </div>
        <button
          type='button'
          className={screen.send}
          onClick={handleSend}
          disabled={sendDisabled}
          title='Отправить сообщение'
          aria-label='Отправить сообщение'
        >
          Отправить
        </button>
      </div>
    </div>
  );
};
